import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BUSINESS } from "../data/regions";
import { ArrowLeft } from "lucide-react";

const PolitiqueConfidentialite = () => {
  useEffect(() => {
    document.title = "Politique de Confidentialité | BKR Toiture";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Politique de confidentialité et gestion des données personnelles du site BKR Toiture.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="politique-confidentialite-page">
      {/* Header */}
      <div className="bg-[#0F172A] text-white py-20 pt-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight font-['Barlow_Condensed']" data-testid="politique-confidentialite-title">
            Politique de Confidentialité
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <div className="bg-white p-8 md:p-12 shadow-sm space-y-8 text-[#334155] leading-relaxed">

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              1. Responsable du traitement
            </h2>
            <p>
              Le responsable du traitement des données personnelles collectées sur ce site est :
            </p>
            <ul className="mt-3 space-y-1 list-none">
              <li><strong>Société :</strong> [Nom de la société]</li>
              <li><strong>Responsable :</strong> [Nom du responsable]</li>
              <li><strong>Adresse :</strong> [Adresse complète]</li>
              <li><strong>Email :</strong> {BUSINESS.email}</li>
              <li><strong>Téléphone :</strong> {BUSINESS.phone}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              2. Données collectées
            </h2>
            <p>
              Nous collectons les données personnelles suivantes lorsque vous utilisez notre formulaire de demande de devis :
            </p>
            <ul className="mt-3 space-y-1 list-disc ml-6">
              <li>Nom et prénom</li>
              <li>Numéro de téléphone</li>
              <li>Adresse email</li>
              <li>Adresse du chantier</li>
              <li>Type de travaux souhaité</li>
              <li>Message complémentaire (optionnel)</li>
            </ul>
            <p className="mt-3">
              Ces données sont collectées lorsque vous remplissez volontairement notre formulaire de contact. 
              Aucune donnée n'est collectée à votre insu.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              3. Finalité du traitement
            </h2>
            <p>Vos données personnelles sont collectées et traitées pour les finalités suivantes :</p>
            <ul className="mt-3 space-y-1 list-disc ml-6">
              <li>Répondre à votre demande de devis</li>
              <li>Vous recontacter pour étudier votre projet</li>
              <li>Établir un devis personnalisé</li>
              <li>Assurer le suivi commercial de votre demande</li>
            </ul>
            <p className="mt-3">
              Vos données ne sont <strong>jamais vendues, louées ou partagées</strong> avec des tiers à des fins commerciales 
              ou publicitaires.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              4. Base légale du traitement
            </h2>
            <p>
              Le traitement de vos données repose sur votre <strong>consentement</strong> (article 6.1.a du RGPD), 
              que vous donnez en soumettant le formulaire de contact, et sur <strong>l'intérêt légitime</strong> 
              (article 6.1.f du RGPD) de l'entreprise à répondre aux demandes commerciales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              5. Durée de conservation
            </h2>
            <p>
              Vos données personnelles sont conservées pour la durée nécessaire à la gestion de votre demande, 
              et au maximum pendant <strong>3 ans</strong> à compter de votre dernier contact avec nous, 
              conformément aux recommandations de la CNIL.
            </p>
            <p className="mt-2">
              Au-delà de cette période, vos données sont supprimées ou anonymisées.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              6. Vos droits
            </h2>
            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi 
              Informatique et Libertés du 6 janvier 1978 modifiée, vous disposez des droits suivants :
            </p>
            <ul className="mt-3 space-y-2 list-disc ml-6">
              <li><strong>Droit d'accès :</strong> obtenir la confirmation que vos données sont traitées et en obtenir une copie</li>
              <li><strong>Droit de rectification :</strong> faire corriger vos données si elles sont inexactes ou incomplètes</li>
              <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données personnelles</li>
              <li><strong>Droit à la limitation :</strong> demander la limitation du traitement de vos données</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré et lisible</li>
            </ul>
            <p className="mt-3">
              Pour exercer ces droits, contactez-nous à :{" "}
              <a href={`mailto:${BUSINESS.email}`} className="text-[#C2410C] hover:underline font-medium">
                {BUSINESS.email}
              </a>
            </p>
            <p className="mt-2">
              En cas de litige, vous pouvez introduire une réclamation auprès de la CNIL :{" "}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#C2410C] hover:underline font-medium">
                www.cnil.fr
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              7. Cookies
            </h2>
            <p>
              Notre site utilise des cookies pour les finalités suivantes :
            </p>
            
            <h3 className="text-lg font-bold text-[#0F172A] mt-4 mb-2 font-['Barlow_Condensed']">
              7.1 Cookies strictement nécessaires
            </h3>
            <p>
              Ces cookies sont indispensables au fonctionnement du site et ne peuvent pas être désactivés. 
              Ils sont généralement activés en réponse à des actions que vous effectuez et qui équivalent 
              à une demande de service (paramétrage de confidentialité, connexion, formulaires).
            </p>

            <h3 className="text-lg font-bold text-[#0F172A] mt-4 mb-2 font-['Barlow_Condensed']">
              7.2 Cookies de mesure d'audience (analytiques)
            </h3>
            <p>
              Ces cookies nous permettent de mesurer l'audience du site et d'analyser la navigation 
              des visiteurs afin d'améliorer nos services. Ces cookies ne sont déposés qu'après votre 
              consentement explicite.
            </p>

            <h3 className="text-lg font-bold text-[#0F172A] mt-4 mb-2 font-['Barlow_Condensed']">
              7.3 Gestion des cookies
            </h3>
            <p>
              Lors de votre première visite, un bandeau vous informe de l'utilisation des cookies et 
              recueille votre consentement. Vous pouvez à tout moment modifier vos choix :
            </p>
            <ul className="mt-2 space-y-1 list-disc ml-6">
              <li>Via le bandeau de consentement affiché sur le site</li>
              <li>Via les paramètres de votre navigateur</li>
            </ul>
            <p className="mt-2">
              La durée de validité du consentement est de <strong>13 mois</strong> maximum, 
              conformément aux recommandations de la CNIL.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              8. Sécurité des données
            </h2>
            <p>
              Nous mettons en oeuvre des mesures techniques et organisationnelles appropriées pour protéger 
              vos données personnelles contre la destruction accidentelle ou illicite, la perte, l'altération, 
              la diffusion ou l'accès non autorisé.
            </p>
            <p className="mt-2">
              Le site utilise le protocole HTTPS pour sécuriser les échanges de données entre votre navigateur 
              et nos serveurs.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0F172A] uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
              9. Modification de la politique
            </h2>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
              Les modifications entreront en vigueur dès leur publication sur cette page. 
              La date de dernière mise à jour est indiquée ci-dessous.
            </p>
          </section>

          <div className="pt-6 border-t border-[#E2E8F0] text-sm text-[#94A3B8]">
            <p>Dernière mise à jour : Janvier 2026</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;
